import { call, cancelled, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { context } from '../../config.js';
import { showMessage } from '../../messages/actions.js';
import handleError from './handle-error.js';

export function getAsyncSaga(actionName, handler) {
  return function* (action) {
    const { meta } = action;

    yield put({ type: `${actionName}_PENDING`, meta });

    try {
      const payload = yield call(handler, action);
      yield put({ type: `${actionName}_FULFILLED`, payload, meta });
    } catch (e) {
      const errorAction = handleError(e);
      if (errorAction) {
        yield put(errorAction);
      }

      yield put({
        type: `${actionName}_REJECTED`,
        error: true,
        payload: e,
        meta
      });
    }
  };
}

export function copyMeta(action, originalAction) {
  return {
    ...action,
    meta: {
      ...action.meta,
      ...originalAction.meta
    }
  };
}

export function getRoutineSaga(routine, handler, options = { handleBasicErrors: true }) {
  return function* (action) {
    try {
      yield put(copyMeta(routine.pending(), action));

      const payload = yield call(handler, action.payload, action.meta);
      yield put(copyMeta(routine.fulfilled(payload), action));
      return payload;
    } catch (error) {
      const errorAction = handleError(error);
      if (options.handleBasicErrors && errorAction) {
        yield put(errorAction);
      }

      if (context.tier === 'dev') {
        console.error(error);
        yield put(showMessage('error', `Error in Routine Saga: "${error.message}" See console for more information`));
      }

      yield put(copyMeta(routine.rejected(error), action));
      return null;
    } finally {
      if (yield cancelled()) {
        yield put(copyMeta(routine.cancelled(), action));
      }
    }
  };
}

export function handleRoutine(routine, handler, options) {
  return takeLatest(routine.REQUESTED, getRoutineSaga(routine, handler, options));
}

export function getBoundRoutineSaga(routine, handler) {
  return function* (action) {
    try {
      yield put(routine.pending(action.meta)());

      const payload = yield call(handler, action);
      yield put(routine.fulfilled(action.meta)(payload));
      return payload;
    } catch (e) {
      const errorAction = handleError(e);
      if (errorAction) {
        yield put(errorAction);
      }

      yield put(routine.rejected(action.meta)(e));
      return null;
    } finally {
      if (yield cancelled()) {
        yield put(routine.cancelled(action.meta)());
      }
    }
  };
}

export function takeLatestRequested(actionName, saga) {
  return takeLatest(`${actionName}_REQUESTED`, saga);
}

export function takeEveryRequested(actionName, saga) {
  return takeEvery(`${actionName}_REQUESTED`, saga);
}
